import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

export const prefix = 'customFields'

export const [LIST_CUSTOM_FIELDS_REQUEST, LIST_CUSTOM_FIELDS_SUCCESS, LIST_CUSTOM_FIELDS_FAILURE] =
  createRequestTypes(prefix, 'LIST_CUSTOM_FIELDS')

export const listCustomFields = createAction(() => ({
  type: [LIST_CUSTOM_FIELDS_REQUEST, LIST_CUSTOM_FIELDS_SUCCESS, LIST_CUSTOM_FIELDS_FAILURE],
  promise: (api: IApi) => api.customFields.list(),
}))

export const [
  CREATE_CUSTOM_FIELD_REQUEST,
  CREATE_CUSTOM_FIELD_SUCCESS,
  CREATE_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'CREATE_CUSTOM_FIELD')

export const createCustomField = createAction((data: object) => ({
  type: [CREATE_CUSTOM_FIELD_REQUEST, CREATE_CUSTOM_FIELD_SUCCESS, CREATE_CUSTOM_FIELD_FAILURE],
  promise: (api: IApi) => api.customFields.create(data),
}))

export const [
  UPDATE_CUSTOM_FIELD_REQUEST,
  UPDATE_CUSTOM_FIELD_SUCCESS,
  UPDATE_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_CUSTOM_FIELD')

export const updateCustomField = createAction((id: string, data: object) => ({
  type: [UPDATE_CUSTOM_FIELD_REQUEST, UPDATE_CUSTOM_FIELD_SUCCESS, UPDATE_CUSTOM_FIELD_FAILURE],
  promise: (api: IApi) => api.customFields.update(id, data),
}))

export const [
  DELETE_CUSTOM_FIELD_REQUEST,
  DELETE_CUSTOM_FIELD_SUCCESS,
  DELETE_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'DELETE_CUSTOM_FIELD')

export const deleteCustomField = createAction((id: string) => ({
  type: [DELETE_CUSTOM_FIELD_REQUEST, DELETE_CUSTOM_FIELD_SUCCESS, DELETE_CUSTOM_FIELD_FAILURE],
  promise: (api: IApi) => api.customFields.delete(id),
}))

// Per-project attachments (item 3). The HTTP lives on the taskProjects API since
// the routes are nested under /task-projects/:id, but the state is kept in this
// domain alongside the library.
export const [
  LIST_PROJECT_CUSTOM_FIELDS_REQUEST,
  LIST_PROJECT_CUSTOM_FIELDS_SUCCESS,
  LIST_PROJECT_CUSTOM_FIELDS_FAILURE,
] = createRequestTypes(prefix, 'LIST_PROJECT_CUSTOM_FIELDS')

export const listProjectCustomFields = createAction((projectId: string) => ({
  type: [
    LIST_PROJECT_CUSTOM_FIELDS_REQUEST,
    LIST_PROJECT_CUSTOM_FIELDS_SUCCESS,
    LIST_PROJECT_CUSTOM_FIELDS_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.listCustomFields(projectId),
}))

export const [
  ATTACH_PROJECT_CUSTOM_FIELD_REQUEST,
  ATTACH_PROJECT_CUSTOM_FIELD_SUCCESS,
  ATTACH_PROJECT_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'ATTACH_PROJECT_CUSTOM_FIELD')

export const attachProjectCustomField = createAction((projectId: string, data: object) => ({
  type: [
    ATTACH_PROJECT_CUSTOM_FIELD_REQUEST,
    ATTACH_PROJECT_CUSTOM_FIELD_SUCCESS,
    ATTACH_PROJECT_CUSTOM_FIELD_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.attachCustomField(projectId, data),
}))

export const [
  UPDATE_PROJECT_CUSTOM_FIELD_REQUEST,
  UPDATE_PROJECT_CUSTOM_FIELD_SUCCESS,
  UPDATE_PROJECT_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_PROJECT_CUSTOM_FIELD')

export const updateProjectCustomField = createAction(
  (projectId: string, fieldId: string, data: object) => ({
    type: [
      UPDATE_PROJECT_CUSTOM_FIELD_REQUEST,
      UPDATE_PROJECT_CUSTOM_FIELD_SUCCESS,
      UPDATE_PROJECT_CUSTOM_FIELD_FAILURE,
    ],
    promise: (api: IApi) => api.taskProjects.updateCustomField(projectId, fieldId, data),
  }),
)

export const [
  REORDER_PROJECT_CUSTOM_FIELDS_REQUEST,
  REORDER_PROJECT_CUSTOM_FIELDS_SUCCESS,
  REORDER_PROJECT_CUSTOM_FIELDS_FAILURE,
] = createRequestTypes(prefix, 'REORDER_PROJECT_CUSTOM_FIELDS')

export const reorderProjectCustomFields = createAction(
  (projectId: string, orderedFieldIds: string[]) => ({
    type: [
      REORDER_PROJECT_CUSTOM_FIELDS_REQUEST,
      REORDER_PROJECT_CUSTOM_FIELDS_SUCCESS,
      REORDER_PROJECT_CUSTOM_FIELDS_FAILURE,
    ],
    promise: (api: IApi) => api.taskProjects.reorderCustomFields(projectId, orderedFieldIds),
  }),
)

export const [
  DETACH_PROJECT_CUSTOM_FIELD_REQUEST,
  DETACH_PROJECT_CUSTOM_FIELD_SUCCESS,
  DETACH_PROJECT_CUSTOM_FIELD_FAILURE,
] = createRequestTypes(prefix, 'DETACH_PROJECT_CUSTOM_FIELD')

export const detachProjectCustomField = createAction((projectId: string, fieldId: string) => ({
  type: [
    DETACH_PROJECT_CUSTOM_FIELD_REQUEST,
    DETACH_PROJECT_CUSTOM_FIELD_SUCCESS,
    DETACH_PROJECT_CUSTOM_FIELD_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.detachCustomField(projectId, fieldId),
}))

// Sets/clears a task's value for one field (item 4). The caller refetches the
// task (showTask / listProjectTasks) to reflect the saved value.
export const [
  SET_TASK_CUSTOM_FIELD_VALUE_REQUEST,
  SET_TASK_CUSTOM_FIELD_VALUE_SUCCESS,
  SET_TASK_CUSTOM_FIELD_VALUE_FAILURE,
] = createRequestTypes(prefix, 'SET_TASK_CUSTOM_FIELD_VALUE')

export const setTaskCustomFieldValue = createAction((taskId: string, data: object) => ({
  type: [
    SET_TASK_CUSTOM_FIELD_VALUE_REQUEST,
    SET_TASK_CUSTOM_FIELD_VALUE_SUCCESS,
    SET_TASK_CUSTOM_FIELD_VALUE_FAILURE,
  ],
  promise: (api: IApi) => api.customFields.setTaskValue(taskId, data),
}))

export const [
  SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST,
  SET_TASKS_CUSTOM_FIELD_VALUE_SUCCESS,
  SET_TASKS_CUSTOM_FIELD_VALUE_FAILURE,
] = createRequestTypes(prefix, 'SET_TASKS_CUSTOM_FIELD_VALUE')

export const setTasksCustomFieldValue = createAction(
  (customFieldDefinitionId: string, data: { taskIds: string[] } & object) => ({
    type: [
      SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST,
      SET_TASKS_CUSTOM_FIELD_VALUE_SUCCESS,
      SET_TASKS_CUSTOM_FIELD_VALUE_FAILURE,
    ],
    promise: (api: IApi) => api.customFields.setTasksValue(customFieldDefinitionId, data),
  }),
)

// GitHub Pull Requests fields: the PRs belong to the task, so they are added and
// removed directly rather than through setTaskCustomFieldValue.
export const [
  ADD_TASK_PULL_REQUEST_REQUEST,
  ADD_TASK_PULL_REQUEST_SUCCESS,
  ADD_TASK_PULL_REQUEST_FAILURE,
] = createRequestTypes(prefix, 'ADD_TASK_PULL_REQUEST')

export const addTaskPullRequest = createAction((taskId: string, url: string) => ({
  type: [
    ADD_TASK_PULL_REQUEST_REQUEST,
    ADD_TASK_PULL_REQUEST_SUCCESS,
    ADD_TASK_PULL_REQUEST_FAILURE,
  ],
  promise: (api: IApi) => api.customFields.addTaskPullRequest(taskId, url),
}))

export const [
  REMOVE_TASK_PULL_REQUEST_REQUEST,
  REMOVE_TASK_PULL_REQUEST_SUCCESS,
  REMOVE_TASK_PULL_REQUEST_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_TASK_PULL_REQUEST')

export const removeTaskPullRequest = createAction((taskId: string, pullRequestId: string) => ({
  type: [
    REMOVE_TASK_PULL_REQUEST_REQUEST,
    REMOVE_TASK_PULL_REQUEST_SUCCESS,
    REMOVE_TASK_PULL_REQUEST_FAILURE,
  ],
  promise: (api: IApi) => api.customFields.removeTaskPullRequest(taskId, pullRequestId),
}))
